import { Col, Container, Image, Row } from 'react-bootstrap';
import SuccessImageOne from 'assets/img/landing/successPathOne.png';
import { PathDetailsList, PathDetailsProp, Props } from './successPath.type';
import Unicon from 'components/base/Unicon';
import { UilArrowUpRight } from '@iconscout/react-unicons';
import { LandingRole } from 'pages/landing/main.type';

function SuccessPath({ role }: Props) {
  const pathDetails: Record<LandingRole, PathDetailsList | null> = {
    student: [
      {
        heading: 'Our Path to Success',
        description: 'Enrollment by Your College',
        image: SuccessImageOne,
        steps: [
          {
            heading: 'Seamless Integration',
            description:
              'Your college automatically enrolls you, eliminating the need for manual registration. Your academic journey begins with just one email.'
          },
          {
            heading: 'Secure Access',
            description:
              'Receive secure login credentials in your inbox, ensuring your personal information remains safe.'
          },
          {
            heading: 'Effortless Onboarding',
            description:
              'Gain access to a user-friendly dashboard designed to streamline your learning and career-building process.'
          },
          {
            heading: 'Start Your Journey',
            description:
              'With your account activated, explore the platform and get ready to upskill and showcase your talent.'
          }
        ]
      },
      {
        heading: 'Our Path to Success',
        description: 'Participate in Assessments & Challenges',
        image: SuccessImageOne,
        steps: [
          {
            heading: 'Skill-based Coding Tests',
            description:
              'Solve coding challenges tailored to your skill level and get instant feedback to improve.'
          },
          {
            heading: 'Hackathons & Competitions',
            description:
              'Participate in hackathons and competitions to apply your skills in real-world scenarios and win recognition.'
          },
          {
            heading: 'Collaborative Projects',
            description:
              'Work on team projects and develop problem-solving skills while building a strong portfolio.'
          },
          {
            heading: 'Continuous Learning',
            description:
              'Tackle challenges in various domains to sharpen your expertise and gain industry-relevant knowledge.'
          }
        ]
      },
      {
        heading: 'Our Path to Success',
        description: 'Earn Certifications & Get Recognized',
        image: SuccessImageOne,
        steps: [
          {
            heading: 'Global Tech Certifications',
            description:
              'Achieve globally recognized certifications in top technologies that validate your expertise.'
          },
          {
            heading: 'Showcase Your Portfolio',
            description:
              'Build a comprehensive portfolio with completed projects and certifications to impress recruiters.'
          },
          {
            heading: 'Gain Industry Recognition',
            description:
              'Stand out in the job market with badges and achievements visible on your profile.'
          },
          {
            heading: 'Build Credibility',
            description:
              'Prove your skills and commitment through your accomplishments on the platform.'
          }
        ]
      },
      {
        heading: 'Our Path to Success',
        description: 'Apply for Jobs & Internships',
        image: SuccessImageOne,
        steps: [
          {
            heading: 'Exclusive Opportunities',
            description:
              'Access a curated list of internships, jobs, and co-op programs from top global recruiters.'
          },
          {
            heading: 'Connect with Recruiters',
            description:
              'Interact directly with hiring managers to showcase your talent and achievements.'
          },
          {
            heading: 'Tailored Job Matches',
            description:
              'Get personalized job recommendations based on your skills, certifications, and interests.'
          },
          {
            heading: 'Kick-start Your Career',
            description:
              'Secure interviews, build professional relationships, and begin your journey in the tech industry.'
          }
        ]
      }
    ],
    recruiter: [
      {
        heading: 'Our Path to Success',
        description: 'Create an Account',
        image: SuccessImageOne,
        steps: [
          {
            heading: 'Seamless Registration',
            description:
              'Sign up quickly and securely to access a pool of talented candidates from top institutions.'
          },
          {
            heading: 'Tailored Dashboard',
            description:
              'Use an intuitive dashboard to streamline your talent search and manage candidates effectively.'
          },
          {
            heading: 'Secure Authentication',
            description:
              'Keep your data and hiring preferences safe with advanced security protocols.'
          },
          {
            heading: 'Begin Your Talent Search',
            description:
              'Start exploring a curated database of skilled students and graduates ready to make an impact.'
          }
        ]
      },
      {
        heading: 'Our Path to Success',
        description: 'Post Opportunities',
        image: SuccessImageOne,
        steps: [
          {
            heading: 'Job & Internship Listings',
            description:
              'Post job and internship opportunities to attract fresh talent with relevant skills'
          },
          {
            heading: 'Custom Requirements',
            description:
              'Define specific skills, certifications, and experience to find the perfect candidates.'
          },
          {
            heading: 'Company Branding',
            description:
              'Highlight your organization with detailed company profiles and employer branding features.'
          },
          {
            heading: 'Wide Reach',
            description:
              'Maximize visibility among thousands of students and colleges connected through CRUD Beat.'
          }
        ]
      },
      {
        heading: 'Our Path to Success',
        description: 'Assess & Shortlist Candidates',
        image: SuccessImageOne,
        steps: [
          {
            heading: 'Custom Assessments',
            description:
              'Design assessments to evaluate candidates’ technical skills and problem-solving abilities.'
          },
          {
            heading: 'Access Portfolios',
            description:
              'Review candidates’ portfolios, certifications, and completed projects to gauge their expertise.'
          },
          {
            heading: 'AI-Powered Matching',
            description:
              'Leverage AI tools to match job requirements with the best-fit candidates.'
          },
          {
            heading: 'Efficient Shortlisting',
            description:
              'Save time with tools to filter and shortlist candidates based on your criteria.'
          }
        ]
      },
      {
        heading: 'Our Path to Success',
        description: 'Hire Top Talent',
        image: SuccessImageOne,
        steps: [
          {
            heading: 'Interview Scheduling',
            description:
              'Use the platform to schedule interviews and communicate directly with potential hires.'
          },
          {
            heading: 'Direct Connections',
            description:
              'Connect with students and recent graduates who align with your organization’s goals.'
          },
          {
            heading: 'Efficient Hiring Process',
            description:
              'Streamline your hiring process with integrated tools for tracking progress and managing offers.'
          },
          {
            heading: 'Build Your Team',
            description:
              'Onboard skilled, job-ready talent who are prepared to contribute from day one.'
          }
        ]
      }
    ],
    college: [
      {
        heading: 'Our Path to Success',
        description: 'Enroll Your Students',
        image: SuccessImageOne,
        steps: [
          {
            heading: 'Centralized Enrollment',
            description:
              'Seamlessly onboard all your students onto CRUD Beat with a simple bulk enrollment process.'
          },
          {
            heading: 'Secure Management',
            description:
              'Manage student accounts and data securely with advanced authentication and privacy controls.'
          },
          {
            heading: 'Customizable Integration',
            description:
              'Integrate CRUD Beat with your existing academic systems for a smooth workflow.'
          },
          {
            heading: 'Empower Your Students',
            description:
              'Provide your students with access to cutting-edge tools and resources to prepare them for the job market.'
          }
        ]
      },
      {
        heading: 'Our Path to Success',
        description: 'Facilitate Skill Development',
        image: SuccessImageOne,
        steps: [
          {
            heading: 'Dedicated Learning Paths',
            description:
              'Enable students to participate in coding assessments, hackathons, and real-world challenges.'
          },
          {
            heading: 'Industry-Driven Curriculum',
            description:
              'Align assessments and projects with current industry trends to prepare students for relevant careers.'
          },
          {
            heading: 'Progress Tracking',
            description:
              'Monitor student performance and skill development through detailed analytics and insights.'
          },
          {
            heading: 'Encourage Collaboration',
            description:
              'Foster teamwork and innovation through collaborative projects and group challenges.'
          }
        ]
      },
      {
        heading: 'Our Path to Success',
        description: 'Highlight Student Achievements',
        image: SuccessImageOne,
        steps: [
          {
            heading: 'Portfolio Building',
            description:
              'Help students create portfolios showcasing certifications, projects, and skills to attract recruiters.'
          },
          {
            heading: 'Global Certifications',
            description:
              'Enable students to earn globally recognized certifications that validate their expertise.'
          },
          {
            heading: 'Institutional Recognition',
            description:
              'Position your institution as a leader in talent development by showcasing student success stories.'
          },
          {
            heading: 'Competitive Advantage',
            description:
              'Promote your institution’s graduates as job-ready candidates with in-demand skills and experience.'
          }
        ]
      },
      {
        heading: 'Our Path to Success',
        description: 'Bridge Students with Recruiters',
        image: SuccessImageOne,
        steps: [
          {
            heading: 'Direct Recruiter Access',
            description:
              'Provide students with direct connections to top recruiters and hiring companies globally.'
          },
          {
            heading: 'Exclusive Job Opportunities',
            description:
              'Collaborate with CRUD Beat to offer students exclusive job and internship listings.'
          },
          {
            heading: 'Streamlined Hiring Process',
            description:
              'Facilitate a seamless hiring process for students by coordinating interviews and applications.'
          },
          {
            heading: 'Strengthen Institutional Reputation',
            description:
              'Build a strong reputation for your institution by producing skilled graduates ready to make an impact.'
          }
        ]
      }
    ],
    default: null
  };
  const successPathDetails = pathDetails[role];

  return (
    <Container fluid className="success">
      {successPathDetails?.map((data: PathDetailsProp, index) => (
        <Row key={`card-${index}`} className="success_cardWrapper">
          <Col className="success_cardWrapper_card bg-body-secondary">
            <Row>
              <Col lg={6}>
                <p className="fs-lg-5 fw-bold fs-md-5 fs-7">{data.heading}</p>
                <p className="text-body-quaternary fs-lg-8 fs-md-8 fs-9">
                  {data.description}
                </p>
              </Col>
              <Col lg={6} className="d-flex justify-content-center">
                <p className="fs-lg-1 fs-md-1 fs-6 fw-semibold gradientText">
                  0{index + 1}
                </p>
              </Col>
            </Row>
            <Row className="pt-4">
              <Col lg={6} className="d-lg-block d-none">
                <Image src={data.image} alt="success-image" />
              </Col>
              <Col>
                {Array.isArray(data.steps) &&
                  data.steps.map((details, idx) => (
                    <div key={`step-${idx}`} className="d-flex">
                      <div>
                        <div className="bg-primary p-1 rounded-circle me-3">
                          <Unicon
                            icon={UilArrowUpRight}
                            color="white"
                            size={20}
                          />
                        </div>
                      </div>
                      <div>
                        <p className="mb-1 fs-lg-7 fs-md-7 fs-8 fw-bold">
                          {details.heading}
                        </p>
                        <p className="mb-4 text-body-secondary fs-lg-8 fs-md-8 fs-9">
                          {details.description}
                        </p>
                      </div>
                    </div>
                  ))}
              </Col>
            </Row>
          </Col>
        </Row>
      ))}
    </Container>
  );
}

export default SuccessPath;
